import { QueryClient, useQuery } from '@tanstack/react-query';
import type { AllocDto, BootstrapDto, ItemDto } from '../shared/api';
import { atMinimum } from '../shared/domain';

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

export const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 15_000, refetchOnWindowFocus: true, retry: 1 } },
});

export async function api<T = unknown>(method: string, url: string, body?: unknown): Promise<T> {
  const r = await fetch(url, {
    method,
    credentials: 'same-origin',
    headers: { 'x-requested-with': 'boghche', ...(body !== undefined ? { 'content-type': 'application/json' } : {}) },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const data = r.headers.get('content-type')?.includes('json') ? await r.json() : null;
  if (!r.ok) {
    if (r.status === 401 && url !== '/api/login') void queryClient.invalidateQueries({ queryKey: ['bootstrap'] });
    throw new ApiError(r.status, data?.error ?? `Error ${r.status}`);
  }
  return data as T;
}
export const get = <T>(url: string) => api<T>('GET', url);
export const post = <T = { ok: true }>(url: string, body: unknown = {}) => api<T>('POST', url, body);
export const patch = <T = { ok: true }>(url: string, body: unknown) => api<T>('PATCH', url, body);
export const del = <T = { ok: true }>(url: string) => api<T>('DELETE', url);

// A tab left open on a warehouse PC must not poll all night: that keeps the Neon database
// awake and uses up the free plan. Polling stops after 10 minutes without use.
const IDLE_MS = 10 * 60_000;
let lastUse = Date.now();
for (const ev of ['pointerdown', 'keydown', 'scroll'])
  window.addEventListener(
    ev,
    () => {
      const wasIdle = Date.now() - lastUse > IDLE_MS;
      lastUse = Date.now();
      if (wasIdle) void queryClient.invalidateQueries({ queryKey: ['bootstrap'] });
    },
    { passive: true },
  );
/** A refetchInterval that pauses while nobody is using the app. */
export const whileInUse = (ms: number) => () => (Date.now() - lastUse > IDLE_MS ? false : ms);

/** Reload all app state after a change. */
export const refresh = () => queryClient.invalidateQueries();

export function useBootstrap() {
  return useQuery({
    queryKey: ['bootstrap'],
    queryFn: () => get<BootstrapDto>('/api/bootstrap'),
    retry: (n, e) => !(e instanceof ApiError && e.status === 401) && n < 1,
    refetchInterval: whileInUse(120_000),
  });
}

/** Lookups over the loaded state. */
export function makeData(b: BootstrapDto) {
  const placeById = new Map(b.places.map((p) => [p.id, p]));
  const shelfById = new Map(b.places.flatMap((p) => p.shelves.map((s) => [s.id, { ...s, place: p }] as const)));
  const where = (placeId: number | null, shelfId: number | null) => {
    if (placeId == null) return '';
    const p = placeById.get(placeId)?.name ?? '?';
    const s = shelfId != null ? shelfById.get(shelfId)?.name : undefined;
    return s ? `${p} › ${s}` : p;
  };
  const used = (i: ItemDto) => i.allocations.reduce((n, a) => n + a.qty, 0);
  const alerts = b.items.filter((i) => atMinimum(i.qty, i.minQty));
  const names = [...new Set(b.items.map((i) => i.name))].sort((x, y) => x.localeCompare(y));
  const isAdmin = b.me.role === 'admin';
  const allocs: (AllocDto & { item: ItemDto })[] = b.items.flatMap((i) => i.allocations.map((a) => ({ ...a, item: i })));
  return { b, me: b.me, isAdmin, placeById, shelfById, where, used, alerts, names, allocs, item: (id: number) => b.items.find((i) => i.id === id) };
}
export type Data = ReturnType<typeof makeData>;
